import { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Cell, ReferenceLine,
  PieChart, Pie, LabelList,
} from 'recharts';
import { fetchData } from '../hooks/useDataRefresh';
import { fmt, fmtPct, calcDelta } from '../components/KPI';

const BRAND_COLORS = {
  'Bhuja': '#f59e0b',
  'Infuzions': '#3b82f6',
  'Infuzions Air Fries': '#06b6d4',
  'Infuzions Multipack': '#8b5cf6',
  'Infuzions Air Fries MP': '#a78bfa',
};
const RETAILER_COLORS = { Woolworths: '#1a8a3f', Coles: '#e03131', Metcash: '#f59e0b' };
const RETAILER_MARKET_SHARE = { Woolworths: 36.8, Coles: 28.2, Metcash: 9.6 };

// ── NIQ Penetration helpers ──────────────────────────────────────────────────

function PenetrationDonut({ brand, color, pen_latest, pen_ya, pen_2ya }) {
  const delta = pen_latest - pen_ya;
  const remaining = 100 - pen_latest;
  const pieData = [
    { name: brand, value: pen_latest, fill: color },
    { name: 'Other', value: remaining, fill: 'var(--border)' },
  ];
  const lyData = [
    { name: 'LY', value: pen_ya, fill: 'var(--text-muted)' },
    { name: 'Other', value: 100 - pen_ya, fill: 'transparent' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{brand}</div>
      <div style={{ position: 'relative', width: 140, height: 140 }}>
        <PieChart width={140} height={140} style={{ position: 'absolute', top: 0, left: 0 }}>
          <Pie data={lyData} cx={65} cy={65} innerRadius={54} outerRadius={60}
            startAngle={90} endAngle={-270} dataKey="value" stroke="none" paddingAngle={0}>
            {lyData.map((d, i) => <Cell key={i} fill={d.fill} />)}
          </Pie>
        </PieChart>
        <PieChart width={140} height={140} style={{ position: 'absolute', top: 0, left: 0 }}>
          <Pie data={pieData} cx={65} cy={65} innerRadius={36} outerRadius={52}
            startAngle={90} endAngle={-270} dataKey="value" stroke="none" paddingAngle={0}>
            {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
          </Pie>
        </PieChart>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
            {pen_latest.toFixed(1)}%
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, marginTop: 3, color: delta >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(2)}pp
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-muted)' }}>
        <span>LY <strong style={{ color: 'var(--text-body)' }}>{pen_ya.toFixed(1)}%</strong></span>
        <span>2YA <strong style={{ color: 'var(--text-muted)' }}>{pen_2ya.toFixed(1)}%</strong></span>
      </div>
    </div>
  );
}

function PenetrationRetailerBar({ brand, color, retailers }) {
  const omni = retailers.find(r => r.retailer === 'Total Outlets Omni');
  const rows = retailers.filter(r => r.retailer !== 'Total Outlets Omni' && r.pen_latest > 0.05);
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{brand}</div>
      {rows.map(r => {
        const delta = r.pen_latest - r.pen_ya;
        const barPct = Math.min((r.pen_latest / (omni?.pen_latest || 25)) * 100, 100);
        const barLyPct = Math.min((r.pen_ya / (omni?.pen_latest || 25)) * 100, 100);
        return (
          <div key={r.retailer} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: 'var(--text-body)' }}>{r.retailer}</span>
              <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{r.pen_latest.toFixed(1)}%</span>
                <span style={{ color: delta >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 500 }}>
                  {delta >= 0 ? '+' : ''}{delta.toFixed(2)}pp
                </span>
              </div>
            </div>
            <div style={{ position: 'relative', height: 5, background: 'var(--border)', borderRadius: 3 }}>
              <div style={{ width: `${barPct}%`, height: '100%', background: color, borderRadius: 3, opacity: 0.8 }} />
              <div style={{ position: 'absolute', left: `${barLyPct}%`, top: -1, width: 2, height: 7, background: 'var(--text-muted)', borderRadius: 1 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Period field helpers ─────────────────────────────────────────────────────

function getSubcatFields(subcat, period) {
  // 13W: direct 13w fields; 52W: direct 52w; 26W: average of 13w×2 (approximate)
  if (period === '52W') {
    return {
      market_ww: subcat.market_rsv_52w_ww ?? subcat.market_rsv_13w_ww * 4,
      market_coles: subcat.market_rsv_52w_coles ?? subcat.market_rsv_13w_coles * 4,
      market_metcash: subcat.metcash_rsv_52w ?? (subcat.metcash_rsv_13w ?? 0) * 4,
      majans_ww: subcat.majans_rsv_52w_ww ?? subcat.majans_rsv_13w_ww * 4,
      majans_coles: subcat.majans_rsv_52w_coles ?? subcat.majans_rsv_13w_coles * 4,
      majans_metcash: subcat.majans_metcash_rsv_52w ?? (subcat.majans_metcash_rsv_13w ?? 0) * 4,
      market_ww_ly: subcat.market_rsv_52w_ww_ly ?? subcat.market_rsv_13w_ww_ly * 4,
      market_coles_ly: subcat.market_rsv_52w_coles_ly ?? subcat.market_rsv_13w_coles_ly * 4,
      market_metcash_ly: subcat.metcash_rsv_52w_ly ?? (subcat.metcash_rsv_13w_ly ?? 0) * 4,
      majans_ww_ly: subcat.majans_rsv_52w_ww_ly ?? subcat.majans_rsv_13w_ww_ly * 4,
      majans_coles_ly: subcat.majans_rsv_52w_coles_ly ?? subcat.majans_rsv_13w_coles_ly * 4,
      majans_metcash_ly: subcat.majans_metcash_rsv_52w_ly ?? (subcat.majans_metcash_rsv_13w_ly ?? 0) * 4,
    };
  }
  if (period === '26W') {
    // Approximate 26W as 2× 13W
    return {
      market_ww: subcat.market_rsv_13w_ww * 2,
      market_coles: subcat.market_rsv_13w_coles * 2,
      market_metcash: (subcat.metcash_rsv_13w ?? 0) * 2,
      majans_ww: subcat.majans_rsv_13w_ww * 2,
      majans_coles: subcat.majans_rsv_13w_coles * 2,
      majans_metcash: (subcat.majans_metcash_rsv_13w ?? 0) * 2,
      market_ww_ly: subcat.market_rsv_13w_ww_ly * 2,
      market_coles_ly: subcat.market_rsv_13w_coles_ly * 2,
      market_metcash_ly: (subcat.metcash_rsv_13w_ly ?? 0) * 2,
      majans_ww_ly: subcat.majans_rsv_13w_ww_ly * 2,
      majans_coles_ly: subcat.majans_rsv_13w_coles_ly * 2,
      majans_metcash_ly: (subcat.majans_metcash_rsv_13w_ly ?? 0) * 2,
    };
  }
  // Default 13W
  return {
    market_ww: subcat.market_rsv_13w_ww,
    market_coles: subcat.market_rsv_13w_coles,
    market_metcash: subcat.metcash_rsv_13w ?? 0,
    majans_ww: subcat.majans_rsv_13w_ww,
    majans_coles: subcat.majans_rsv_13w_coles,
    majans_metcash: subcat.majans_metcash_rsv_13w ?? 0,
    market_ww_ly: subcat.market_rsv_13w_ww_ly,
    market_coles_ly: subcat.market_rsv_13w_coles_ly,
    market_metcash_ly: subcat.metcash_rsv_13w_ly ?? 0,
    majans_ww_ly: subcat.majans_rsv_13w_ww_ly,
    majans_coles_ly: subcat.majans_rsv_13w_coles_ly,
    majans_metcash_ly: subcat.majans_metcash_rsv_13w_ly ?? 0,
  };
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function MarketTrends() {
  const [market, setMarket] = useState(null);
  const [brandData, setBrandData] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [niq, setNiq] = useState(null);
  const [period, setPeriod] = useState('13W');
  const [retailer, setRetailer] = useState('Both');

  useEffect(() => {
    Promise.all([
      fetchData('scantg-market.json'),
      fetchData('scanv2-brand.json'),
      fetchData('scanv2-weekly.json'),
      fetchData('niq-penetration.json'),
    ]).then(([m, b, w, n]) => {
      setMarket(m);
      setBrandData(Array.isArray(b) ? b : []);
      setWeekly(Array.isArray(w) ? w : []);
      setNiq(n);
    });
  }, []);

  const weekCount = period === '52W' ? 52 : period === '26W' ? 26 : 13;

  // Brand growth cards
  const brandGrowth = useMemo(() => brandData.map(b => {
    const ty = (b.ww_ytd_ty || 0) + (b.coles_ytd_ty || 0);
    const ly = (b.ww_ytd_ly || 0) + (b.coles_ytd_ly || 0);
    return {
      brand: b.family,
      label: b.family.replace('Infuzions Multipack', 'Infuzions MP').replace('Infuzions Air Fries MP', 'Air Fries MP'),
      ty, ly,
      ww_ty: b.ww_ytd_ty || 0, ww_ly: b.ww_ytd_ly || 0,
      coles_ty: b.coles_ytd_ty || 0, coles_ly: b.coles_ytd_ly || 0,
      growth: calcDelta(ty, ly),
      ww_growth: calcDelta(b.ww_ytd_ty, b.ww_ytd_ly),
      coles_growth: calcDelta(b.coles_ytd_ty, b.coles_ytd_ly),
    };
  }), [brandData]);

  // Weekly share trend
  const shareTrend = useMemo(() => {
    if (!weekly.length) return [];
    return weekly.slice(-weekCount).map(d => ({
      date: (d.date_ty || '').slice(5, 10),
      ww_share: d.ww_cat ? (d.ww_ty / d.ww_cat) * 100 : null,
      coles_share: d.coles_cat ? (d.coles_ty / d.coles_cat) * 100 : null,
    }));
  }, [weekly, weekCount]);

  // Sub-category share
  const subcatData = useMemo(() => {
    if (!market?.subcategories) return [];
    return market.subcategories.map(s => {
      const f = getSubcatFields(s, period);
      return {
        name: s.name,
        brand: s.brand,
        ww_share: f.market_ww > 0 ? (f.majans_ww / f.market_ww) * 100 : 0,
        coles_share: f.market_coles > 0 ? (f.majans_coles / f.market_coles) * 100 : 0,
        metcash_share: f.market_metcash > 0 ? (f.majans_metcash / f.market_metcash) * 100 : 0,
        ww_share_ly: f.market_ww_ly > 0 ? (f.majans_ww_ly / f.market_ww_ly) * 100 : 0,
        coles_share_ly: f.market_coles_ly > 0 ? (f.majans_coles_ly / f.market_coles_ly) * 100 : 0,
        metcash_share_ly: f.market_metcash_ly > 0 ? (f.majans_metcash_ly / f.market_metcash_ly) * 100 : 0,
        majans_ww: f.majans_ww,
        majans_coles: f.majans_coles,
        majans_metcash: f.majans_metcash,
      };
    });
  }, [market, period]);

  // Fair share rows
  const fairShareRows = useMemo(() => {
    const shareData = market?.customer_brand_share || [];
    const rows = [];
    const brands = [...new Set(shareData.map(s => s.brand))];
    const periodScale = period === '52W' ? 4 : period === '26W' ? 2 : 1;
    brands.forEach(brand => {
      const allForBrand = shareData.filter(s => s.brand === brand);
      const totalRsv = allForBrand.reduce((sum, s) => sum + s.majans_rsv_ytd, 0);
      const retailers = ['Woolworths', 'Coles', 'Metcash'].filter(r => {
        if (retailer === 'Both') return true;
        if (retailer === 'Metcash') return r === 'Metcash';
        return r === retailer || r !== 'Metcash';
      });
      retailers.forEach(ret => {
        const row = shareData.find(s => s.brand === brand && s.retailer === ret);
        if (!row) return;
        if (retailer !== 'Both' && retailer !== 'Metcash' && ret === 'Metcash') return;
        if (retailer === 'Metcash' && ret !== 'Metcash') return;
        if (retailer !== 'Both' && ret !== retailer) return;
        const rsv = row.majans_rsv_ytd * periodScale;
        const brandShareAtRetailer = totalRsv > 0 ? (row.majans_rsv_ytd / totalRsv) * 100 : 0;
        const retailerMarketShare = RETAILER_MARKET_SHARE[ret] || 9.6;
        const fsi = retailerMarketShare > 0 ? brandShareAtRetailer / retailerMarketShare : 0;
        rows.push({ brand, retailer: ret, brandShare: brandShareAtRetailer, retailerMarketShare, fsi, rsv });
      });
    });
    return rows;
  }, [market, retailer, period]);

  // Top brands for selected retailer(s)
  const topBrands = useMemo(() => {
    const tb = market?.top_brands_13w;
    if (!tb) return [];
    let data = [];
    if (retailer === 'Both') {
      // Merge all three retailers, sum values
      const map = {};
      ['Woolworths', 'Coles', 'Metcash'].forEach(r => {
        (tb[r] || []).forEach(b => {
          if (!map[b.brand]) map[b.brand] = { brand: b.brand, ty: 0, ly: 0 };
          map[b.brand].ty += b.ty;
          map[b.brand].ly += b.ly;
        });
      });
      data = Object.values(map).sort((a, b) => (b.ty - b.ly) - (a.ty - a.ly)).slice(0, 10);
    } else {
      data = (tb[retailer] || []).slice(0, 10);
    }
    return data.map(b => ({
      ...b,
      growth: calcDelta(b.ty, b.ly),
      label: b.brand.length > 16 ? b.brand.slice(0, 15) + '…' : b.brand,
    }));
  }, [market, retailer]);

  const dataEndDate = market?.data_end_date
    ? (() => {
        const d = new Date(market.data_end_date + 'T00:00:00');
        return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      })()
    : null;

  const showWW = retailer === 'Both' || retailer === 'Woolworths';
  const showColes = retailer === 'Both' || retailer === 'Coles';
  const showMetcash = retailer === 'Both' || retailer === 'Metcash';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, margin: 0 }}>Market Trends</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            SCAN TOTAL GROCERY — category share, brand growth &amp; fair share index
            {dataEndDate && (
              <span style={{ marginLeft: 12, color: 'var(--text-muted)' }}>
                Data to week ending Tuesday {dataEndDate}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Period</span>
          {['13W', '26W', '52W'].map(p => (
            <button key={p} className={period === p ? 'pill-active' : 'pill-inactive'} onClick={() => setPeriod(p)}>{p}</button>
          ))}
          <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 4px' }} />
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Retailer</span>
          {['Both', 'Woolworths', 'Coles', 'Metcash'].map(r => (
            <button key={r} className={retailer === r ? 'pill-active' : 'pill-inactive'} onClick={() => setRetailer(r)}>{r}</button>
          ))}
        </div>
      </div>


      {/* ── Brand Growth KPI Cards ── */}
      {brandGrowth.length > 0 ? (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {brandGrowth.map(b => (
            <div key={b.brand} className="kpi-card" style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 11, color: BRAND_COLORS[b.brand] || 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                {b.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(b.ty)}</div>
              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: (b.growth ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                  {b.growth != null ? ((b.growth >= 0 ? '+' : '') + b.growth.toFixed(1) + '%') : '—'} Total
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                <span style={{ color: (b.ww_growth ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                  WW {b.ww_growth != null ? ((b.ww_growth >= 0 ? '+' : '') + b.ww_growth.toFixed(1) + '%') : '—'}
                </span>
                <span>|</span>
                <span style={{ color: (b.coles_growth ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                  Coles {b.coles_growth != null ? ((b.coles_growth >= 0 ? '+' : '') + b.coles_growth.toFixed(1) + '%') : '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '16px 0' }}>Loading brand data…</div>
      )}

      {/* ── Brand Growth Chart + Sub-category Share ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Brand growth bar */}
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>Brand Growth — TY vs LY (YTD, WW + Coles)</div>
          {brandGrowth.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={brandGrowth} margin={{ left: 8, right: 8, top: 4, bottom: 4 }} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: 'var(--text-body)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => `$${(v / 1e6).toFixed(0)}M`} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} width={44} />
                <Tooltip formatter={(v, n) => [fmt(v), n]} contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="ly" name="LY" fill="var(--border-soft)" radius={[2, 2, 0, 0]} />
                <Bar dataKey="ty" name="TY" radius={[2, 2, 0, 0]}>
                  {brandGrowth.map((b, i) => <Cell key={i} fill={BRAND_COLORS[b.brand] || '#3b82f6'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Loading…</div>
          )}
        </div>

        {/* Sub-category share */}
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Sub-Category Market Share ({period})</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>Source: SCAN TOTAL GROCERY (DEMAND workspace)</div>
          {subcatData.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading…</div>
          ) : (
            subcatData.map(s => {
              const retailers = [
                showWW && { label: 'WW', share: s.ww_share, ly: s.ww_share_ly, color: '#1a8a3f' },
                showColes && { label: 'Coles', share: s.coles_share, ly: s.coles_share_ly, color: '#e03131' },
                showMetcash && { label: 'IGA', share: s.metcash_share, ly: s.metcash_share_ly, color: '#f59e0b' },
              ].filter(Boolean);
              return (
                <div key={s.name} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: BRAND_COLORS[s.brand] || 'var(--text-primary)', fontWeight: 600 }}>{s.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.brand}</span>
                  </div>
                  {retailers.map(r => (
                    <div key={r.label} style={{ marginBottom: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.label}</span>
                        <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{r.share.toFixed(1)}%</span>
                          <span style={{ color: r.share >= r.ly ? 'var(--success)' : 'var(--danger)' }}>
                            {r.share >= r.ly ? '▲' : '▼'} {Math.abs(r.share - r.ly).toFixed(1)}pp vs LY
                          </span>
                        </div>
                      </div>
                      <div style={{ position: 'relative', height: 6, background: 'var(--border)', borderRadius: 3 }}>
                        <div style={{ width: `${Math.min(r.share * 5, 100)}%`, height: '100%', background: r.color, borderRadius: 3 }} />
                        <div style={{ position: 'absolute', left: `${Math.min(r.ly * 5, 100)}%`, top: -2, width: 2, height: 10, background: 'var(--text-muted)', borderRadius: 1 }} />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Weekly Share Trend + Fair Share ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Weekly trend */}
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
            Majans Overall Category Share — {period} Weekly Trend
          </div>
          {shareTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={shareTrend} margin={{ left: 4, right: 8, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} interval={Math.floor(weekCount / 6)} />
                <YAxis tickFormatter={v => `${v.toFixed(1)}%`} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
                <Tooltip formatter={v => v != null ? `${v.toFixed(2)}%` : '—'} contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {showWW && (
                  <Line dataKey="ww_share" name="Woolworths" stroke="#1a8a3f" strokeWidth={2} dot={false} connectNulls />
                )}
                {showColes && (
                  <Line dataKey="coles_share" name="Coles" stroke="#e03131" strokeWidth={2} dot={false} connectNulls />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Loading weekly data…</div>
          )}
        </div>

        {/* Fair Share Index */}
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Customer Brand Share vs Fair Share</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>
            Brand share at retailer ÷ retailer grocery market share · FSI = 1.0 is perfectly proportional
          </div>
          {fairShareRows.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading…</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Retailer</th>
                  <th style={{ textAlign: 'right' }}>RSV</th>
                  <th style={{ textAlign: 'right' }}>Br. Share</th>
                  <th style={{ textAlign: 'right' }}>Mkt Share</th>
                  <th style={{ textAlign: 'right' }}>FSI</th>
                </tr>
              </thead>
              <tbody>
                {fairShareRows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ color: BRAND_COLORS[r.brand] || 'var(--text-primary)', fontWeight: 500, fontSize: 11 }}>
                      {r.brand.replace('Infuzions Multipack', 'Infuzions MP').replace('Infuzions Air Fries MP', 'AF MP')}
                    </td>
                    <td style={{ fontSize: 11, color: RETAILER_COLORS[r.retailer] || '#f59e0b' }}>{r.retailer}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-body)', fontSize: 11 }}>{fmt(r.rsv)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-primary)', fontSize: 11 }}>{r.brandShare.toFixed(1)}%</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-body)', fontSize: 11 }}>{r.retailerMarketShare.toFixed(1)}%</td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: r.fsi >= 1.0 ? 'var(--success)' : r.fsi >= 0.8 ? 'var(--warn)' : 'var(--danger)',
                      }}>
                        {r.fsi.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            FSI &gt;1.0 green · 0.8–1.0 amber · &lt;0.8 red
          </div>
        </div>
      </div>

      {/* ── Top 10 Fastest Growing Brands ── */}
      {topBrands.length > 0 && (
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
            Top 10 Brands by Value — {retailer === 'Both' ? 'All Retailers' : retailer} ({period})
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>
            Sorted by absolute RSV. Source: SCAN TOTAL GROCERY. 52-week totals.
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              layout="vertical"
              data={[...topBrands].sort((a, b) => b.ty - a.ty)}
              margin={{ left: 100, right: 110, top: 4, bottom: 4 }}
              barGap={4}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <XAxis type="number" tickFormatter={v => `$${(v / 1e6).toFixed(0)}M`} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="label" tick={{ fill: 'var(--text-body)', fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
              <Tooltip
                formatter={(v, n) => [fmt(v), n]}
                contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 11 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="ly" name="LY" fill="var(--border-soft)" radius={[0, 2, 2, 0]}>
                {topBrands.map((b, i) => <Cell key={i} fill="var(--border-soft)" />)}
              </Bar>
              <Bar dataKey="ty" name="TY" radius={[0, 2, 2, 0]}>
                {topBrands.map((b, i) => {
                  const isMajans = ['Bhuja', 'Infuzions', 'Infuzions Air Fries', 'Infuzions Multipack', 'Infuzions Air Fries MP'].includes(b.brand);
                  const growing = (b.growth ?? 0) >= 0;
                  const color = isMajans
                    ? (BRAND_COLORS[b.brand] || '#f59e0b')
                    : (growing ? 'var(--info)' : 'var(--text-muted)');
                  return <Cell key={i} fill={color} />;
                })}
                <LabelList
                  content={({ x, y, width, height, index }) => {
                    const b = [...topBrands].sort((a, c) => c.ty - a.ty)[index];
                    if (!b) return null;
                    const sign = (b.growth ?? 0) >= 0 ? '+' : '';
                    const growthTxt = b.growth != null ? `${sign}${b.growth.toFixed(1)}%` : '';
                    const dollarGrowth = b.ty - b.ly;
                    const dSign = dollarGrowth >= 0 ? '+' : '-';
                    const dollarTxt = `${dSign}$${(Math.abs(dollarGrowth) / 1e6).toFixed(1)}M`;
                    const color = (b.growth ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)';
                    return (
                      <text x={x + width + 6} y={y + height / 2} fill={color} fontSize={10} dominantBaseline="middle">
                        {growthTxt} {dollarTxt}
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {/* Growth rate labels legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {[...topBrands].sort((a, b) => b.ty - a.ty).map(b => (
              <span key={b.brand} style={{
                fontSize: 11,
                background: 'var(--border)',
                borderRadius: 4,
                padding: '2px 8px',
                color: (b.growth ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)',
              }}>
                {b.brand} {b.growth != null ? ((b.growth >= 0 ? '+' : '') + b.growth.toFixed(1) + '%') : '—'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Top 10 Manufacturers ── */}
      {market?.top_manufacturers?.length > 0 && (
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
            Top 10 Manufacturers — {retailer === 'Both' ? 'All Retailers' : retailer} (52W)
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>
            Retail scan value by manufacturer. Source: SCAN TOTAL GROCERY.
          </div>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Manufacturer</th>
                <th style={{ textAlign: 'right' }}>TY RSV</th>
                <th style={{ textAlign: 'right' }}>LY RSV</th>
                <th style={{ textAlign: 'right' }}>Growth</th>
                <th style={{ textAlign: 'right', minWidth: 80 }}>Share</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const mfrs = market.top_manufacturers;
                const totalTY = mfrs.reduce((s, m) => s + m.ty, 0);
                return mfrs.map((m, i) => {
                  const growth = m.ly > 0 ? ((m.ty - m.ly) / m.ly) * 100 : null;
                  const sharePct = totalTY > 0 ? (m.ty / totalTY) * 100 : 0;
                  const isMajans = m.name === 'MAJANS';
                  return (
                    <tr key={i}>
                      <td style={{ fontSize: 12, color: isMajans ? '#f59e0b' : 'var(--text-primary)', fontWeight: isMajans ? 700 : 400 }}>
                        {isMajans ? '★ ' : ''}{m.name}
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--text-primary)', fontSize: 12 }}>
                        ${(m.ty / 1e6).toFixed(0)}M
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>
                        ${(m.ly / 1e6).toFixed(0)}M
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 600, color: growth == null ? 'var(--text-muted)' : growth >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {growth == null ? '—' : `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 11 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                          <div style={{ width: 60, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(sharePct * 2, 100)}%`, height: '100%', background: isMajans ? '#f59e0b' : '#3b82f6', borderRadius: 2 }} />
                          </div>
                          <span style={{ color: 'var(--text-body)', minWidth: 32, textAlign: 'right' }}>{sharePct.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* ── NIQ Household Penetration ── */}
      {niq && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>Household Penetration</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                NielsenIQ Omnishopper · Total Outlets Omni · {niq.period_latest}
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
              <div>Inner ring = Latest &nbsp;|&nbsp; Outer ring = LY</div>
              <div style={{ marginTop: 2 }}>Delta vs prior year (pp)</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 32 }}>
              {niq.brands.map(b => {
                const omni = b.retailers.find(r => r.retailer === 'Total Outlets Omni');
                return omni ? (
                  <PenetrationDonut key={b.brand}
                    brand={b.brand} color={b.color}
                    pen_latest={omni.pen_latest} pen_ya={omni.pen_ya} pen_2ya={omni.pen_2ya}
                  />
                ) : null;
              })}
            </div>
            <div style={{ width: 1, background: 'var(--border)', alignSelf: 'stretch', minHeight: 120 }} />
            <div style={{ display: 'flex', gap: 32, flex: 1, flexWrap: 'wrap' }}>
              {niq.brands.map(b => (
                <PenetrationRetailerBar key={b.brand} brand={b.brand} color={b.color} retailers={b.retailers} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer note ── */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
        <strong style={{ color: 'var(--text-primary)' }}>Data sources:</strong>{' '}
        Brand growth &amp; weekly share — SCANv2 (Majans SKUs, WW + Coles) · Week ending <strong style={{ color: 'var(--text-body)' }}>17 Mar 2026</strong>.{' '}
        Sub-category market share — <strong style={{ color: 'var(--text-primary)' }}>SCAN TOTAL GROCERY</strong> (DEMAND workspace) · currently requires auth refresh.{' '}
        HH Penetration — NielsenIQ Omnishopper Panel, {niq?.period_latest || 'N/A'}.{' '}
        Top brands — SCAN TOTAL GROCERY estimates.
      </div>
    </div>
  );
}
